import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Form, Spinner } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import { OPCIONES_BASE, EJES_BASE, datasetBarrasSimple } from '../utils/graficos';
import TarjetaKpi from '../components/ui/TarjetaKpi';
import * as reportesReferenciasService from '../services/reportes/reportesReferenciasService';
import '../style/pages/HomePage.css';
import useBreakpoint from '../hooks/useBreakpoint';
import BottomSheet from '../components/mobile/BottomSheet';
import BotonFiltros from '../components/mobile/BotonFiltros';
import ChipsFiltros from '../components/mobile/ChipsFiltros';
import { coincideBusqueda } from '../utils/textoComparable';
import PageHeader from '../components/ui/PageHeader';





const formatearFechaMientrasEscribe = (valor) => {
    const n = valor.replace(/\D/g, '');
    if (!n.length) return '';
    if (n.length <= 2) return n;
    if (n.length <= 4) return `${n.slice(0, 2)}/${n.slice(2)}`;
    return `${n.slice(0, 2)}/${n.slice(2, 4)}/${n.slice(4, 8)}`;
};

const convertirTextoAFecha = (texto) => {
    if (!texto?.trim()) return null;
    const partes = texto.replace(/[^\d/]/g, '').split('/');
    if (partes.length === 3) {
        const [dia, mes, anio] = partes.map(Number);
        if (dia > 0 && dia <= 31 && mes > 0 && mes <= 12 && anio >= 1900 && anio <= 2100) {
            const f = new Date(anio, mes - 1, dia);
            if (!isNaN(f.getTime()) && f.getDate() === dia && f.getMonth() === mes - 1)
                return f.toISOString().split('T')[0];
        }
    }
    return null;
};

function ReportesReferencias() {
    const navigate = useNavigate();

    const [resumen, setResumen] = useState(null);
    const [detalle, setDetalle] = useState([]);
    const [loading, setLoading] = useState(true);

    const [filtroFechaInicio, setFiltroFechaInicio] = useState('');
    const [filtroFechaFin, setFiltroFechaFin] = useState('');
    const [filtroFechaInicioTexto, setFiltroFechaInicioTexto] = useState('');
    const [filtroFechaFinTexto, setFiltroFechaFinTexto] = useState('');

    const [busqueda, setBusqueda] = useState('');
    const [sheetFiltrosAbierto, setSheetFiltrosAbierto] = useState(false);
    const { esMobile } = useBreakpoint();

    useEffect(() => {
        const cargar = async () => {
            try {
                setLoading(true);
                const filtros = {
                    fechaDesde: filtroFechaInicio || null,
                    fechaHasta: filtroFechaFin || null,
                };
                const data = await reportesReferenciasService.getAllReportes(filtros);
                setResumen(data.resumen);
                setDetalle(data.detalle);
            } catch (err) {
                console.error('Error cargando reportes de referencias:', err);
            } finally {
                setLoading(false);
            }
        };
        cargar();
    }, [filtroFechaInicio, filtroFechaFin]);

    const detalleFiltrado = detalle.filter((r) => {
        return coincideBusqueda([r.nombre, r.nombreUsuario, r.descripcion], busqueda);
    });

    const datosBarClientes = {
        labels: detalleFiltrado.map((r) => r.nombre),
        datasets: [
            datasetBarrasSimple('Clientes referidos', detalleFiltrado.map((r) => r.totalClientes)),
        ],
    };

    // Una sola medida (clientes por referencia): todas las barras del mismo color.
    const opcionesBar = { ...OPCIONES_BASE, plugins: { ...OPCIONES_BASE.plugins, legend: { display: false } }, scales: { x: EJES_BASE.x, y: { ...EJES_BASE.y, ticks: { ...EJES_BASE.y.ticks, precision: 0 } } } };

    const limpiarFiltros = () => {
        setFiltroFechaInicioTexto('');
        setFiltroFechaFinTexto('');
        setFiltroFechaInicio('');
        setFiltroFechaFin('');
        setBusqueda('');
    };

    const kpisReferencias = [
        { label: 'Total referencias', valor: resumen?.totalReferencias ?? 0, sub: null, icono: 'signpost-split' },
        { label: 'Clientes referidos', valor: resumen?.totalClientesReferidos ?? 0, sub: null, icono: 'people', tono: 'exito' },
        { label: 'Total trámites', valor: resumen?.totalTramites ?? 0, sub: null, icono: 'folder2-open', tono: 'advertencia' },
        {
            label: 'Referencia más productiva',
            valor: resumen?.referenciaMasProductivaNombre ?? '—',
            sub: resumen?.referenciaMasProductivaCantidad > 0 ? `${resumen.referenciaMasProductivaCantidad} clientes` : null,
            icono: 'trophy', tono: 'violeta', esTexto: true,
        },
    ];

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
                <Spinner animation="border" style={{ color: '#7c3aed' }} />
                <span className="ms-3">Consultando reportes al servidor...</span>
            </div>
        );
    }

    return (
        <div className="homepage-container area-reportes">
            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <span
                            role="button"
                            style={{ cursor: 'pointer', color: '#3B82F6', textDecoration: 'underline' }}
                            onClick={() => navigate('/reportes')}
                        >
                            Reportes
                        </span>
                    </li>
                    <li className="breadcrumb-item active">Referencias</li>
                </ol>
            </nav>

            {/* Header */}
            <PageHeader
                variante="plano"
                icono="diagram-3-fill"
                titulo="Reportes por Referencia"
                descripcion="Productividad y clientes agrupados por referencia"
            />

            {/* Filtros */}
            {(() => {
                const campoDesde = (
                    <Form.Group>
                        <Form.Label >
                            <i className="bi bi-calendar-event me-1" />Fecha Desde
                        </Form.Label>
                        <Form.Control
                            placeholder="dd/mm/aaaa"
                            value={filtroFechaInicioTexto}
                            onChange={(e) => {
                                const v = formatearFechaMientrasEscribe(e.target.value);
                                setFiltroFechaInicioTexto(v);
                                const iso = convertirTextoAFecha(v);
                                setFiltroFechaInicio(iso || '');
                            }}
                            
                        />
                    </Form.Group>
                );
                const campoHasta = (
                    <Form.Group>
                        <Form.Label >
                            <i className="bi bi-calendar-x me-1" />Fecha Hasta
                        </Form.Label>
                        <Form.Control
                            placeholder="dd/mm/aaaa"
                            value={filtroFechaFinTexto}
                            onChange={(e) => {
                                const v = formatearFechaMientrasEscribe(e.target.value);
                                setFiltroFechaFinTexto(v);
                                const iso = convertirTextoAFecha(v);
                                setFiltroFechaFin(iso || '');
                            }}
                            
                        />
                    </Form.Group>
                );
                const campoBusqueda = (
                    <Form.Group>
                        <Form.Label >
                            <i className="bi bi-search me-1" />Buscar referencia
                        </Form.Label>
                        <Form.Control
                            placeholder="Nombre, usuario o descripción..."
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            
                        />
                    </Form.Group>
                );

                const chipsFiltros = [
                    filtroFechaInicioTexto && { key: 'desde', label: `Desde: ${filtroFechaInicioTexto}`, onQuitar: () => { setFiltroFechaInicioTexto(''); setFiltroFechaInicio(''); } },
                    filtroFechaFinTexto && { key: 'hasta', label: `Hasta: ${filtroFechaFinTexto}`, onQuitar: () => { setFiltroFechaFinTexto(''); setFiltroFechaFin(''); } },
                    busqueda && { key: 'busqueda', label: busqueda, onQuitar: () => setBusqueda('') },
                ].filter(Boolean);

                return esMobile ? (
                    <div className="eventos-section">
                        <div className="eventos-header-row">
                            <h2 className="eventos-section-title">
                                <i className="bi bi-funnel me-2" />Filtros
                            </h2>
                        </div>
                        <BotonFiltros cantidad={chipsFiltros.length} onClick={() => setSheetFiltrosAbierto(true)} />
                        <ChipsFiltros chips={chipsFiltros} />
                        {sheetFiltrosAbierto && (
                            <BottomSheet
                                titulo="Filtros"
                                onCerrar={() => setSheetFiltrosAbierto(false)}
                                accionPrimaria={(filtroFechaInicioTexto || filtroFechaFinTexto) ? { label: 'Limpiar filtros', onClick: limpiarFiltros } : undefined}
                            >
                                <div className="mb-3">{campoDesde}</div>
                                <div className="mb-3">{campoHasta}</div>
                                {campoBusqueda}
                            </BottomSheet>
                        )}
                    </div>
                ) : (
                    <div className="eventos-section">
                        <div className="eventos-header-row">
                            <h2 className="eventos-section-title">
                                <i className="bi bi-funnel me-2" />Filtros
                            </h2>
                            <button
                                type="button"
                                className="btn-ver-calendario-home"
                                
                                onClick={limpiarFiltros}
                            >
                                <i className="bi bi-x-circle me-2" />Limpiar filtros
                            </button>
                        </div>
                        <Row className="g-3">
                            <Col md={4}>{campoDesde}</Col>
                            <Col md={4}>{campoHasta}</Col>
                            <Col md={4}>{campoBusqueda}</Col>
                        </Row>
                    </div>
                );
            })()}

            {/* KPIs */}
            <Row className="mb-4 g-3">
                {kpisReferencias.map((kpi) => (
                    <Col md={3} key={kpi.label}>
                        <TarjetaKpi {...kpi} />
                    </Col>
                ))}
            </Row>

            {/* Gráfico */}
            {detalleFiltrado.length > 0 && (
                <div className="eventos-section">
                    <h2 className="eventos-section-title" style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>
                        <i className="bi bi-bar-chart-fill me-2" />Clientes por Referencia
                    </h2>
                    <p className="text-muted small mb-3">Cantidad de clientes referidos por cada referencia, en el período filtrado.</p>
                    <Bar data={datosBarClientes} options={opcionesBar} />
                </div>
            )}

            {/* Tabla de detalle */}
            <div className="eventos-section">
                <div className="eventos-header-row">
                    <div>
                        <h2 className="eventos-section-title" style={{ fontSize: '1.2rem', marginBottom: '0.25rem' }}>
                            <i className="bi bi-table me-2" />Detalle por Referencia
                        </h2>
                        <p className="text-muted small mb-0">Clientes y trámites (RTI/ART) generados por cada referencia, con el total acumulado.</p>
                    </div>
                    <span className="badge bg-secondary">{detalleFiltrado.length} resultado{detalleFiltrado.length !== 1 ? 's' : ''}</span>
                </div>
                {detalleFiltrado.length === 0 ? (
                    <div className="empty-state-eventos compact">
                        <i className="bi bi-diagram-3" />
                        <p className="mb-0">No hay datos para los filtros seleccionados.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>Referencia</th>
                                    <th>Descripción</th>
                                    <th>Usuario asignado</th>
                                    <th className="text-center">Clientes</th>
                                    <th className="text-center">RTI</th>
                                    <th className="text-center">ART</th>
                                    <th className="text-center fw-bold">Total trámites</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detalleFiltrado.map((r) => (
                                    <tr key={r.idReferencia}>
                                        <td>
                                            <span
                                                className="badge rounded-pill me-2"
                                                style={{ backgroundColor: r.color || '#6c757d', width: 12, height: 12, display: 'inline-block', verticalAlign: 'middle' }}
                                            />
                                            <span className="fw-semibold">{r.nombre}</span>
                                        </td>
                                        <td className="text-muted small">{r.descripcion || '—'}</td>
                                        <td>{r.nombreUsuario || '—'}</td>
                                        <td className="text-center">{r.totalClientes}</td>
                                        <td className="text-center text-primary">{r.tramitesRTI}</td>
                                        <td className="text-center text-warning">{r.tramitesART}</td>
                                        <td className="text-center fw-bold">{r.totalTramites}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr className="fw-bold">
                                    <td colSpan={3}>Total</td>
                                    <td className="text-center">{detalleFiltrado.reduce((s, r) => s + Number(r.totalClientes), 0)}</td>
                                    <td className="text-center text-primary">{detalleFiltrado.reduce((s, r) => s + Number(r.tramitesRTI), 0)}</td>
                                    <td className="text-center text-warning">{detalleFiltrado.reduce((s, r) => s + Number(r.tramitesART), 0)}</td>
                                    <td className="text-center">{detalleFiltrado.reduce((s, r) => s + Number(r.totalTramites), 0)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ReportesReferencias;
